import {ToolEngineEnum} from "@shared/GenericTools/ToolEngineEnum.js";
import {hasRequiredEngineInterfaces} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceToken";
import type {BaseToolEngineInterface} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceToken";
import FormToolEngine from "@shared/GenericTools/engines/FormToolEngine";
import ItemListToolEngine from "@shared/GenericTools/engines/ItemListToolEngine";
import TrackerToolEngine from "@shared/GenericTools/engines/TrackerToolEngine";
import GraphToolEngine from "@shared/GenericTools/engines/GraphToolEngine";
import StagedWorksheetToolEngine from "@shared/GenericTools/engines/StagedWorksheetToolEngine";
import TimelineToolEngine from "@shared/GenericTools/engines/TimelineToolEngine";
import CanvasTimelineToolEngine from "@shared/GenericTools/engines/CanvasTimelineToolEngine";
import BaseToolEngine from "@shared/GenericTools/engines/BaseToolEngine";

type ToolEngineInterfaceMap = Readonly<Record<string, BaseToolEngineInterface>>
type ToolEngineApi = Record<string, BaseToolEngineInterface>

type ToolEngineDefinition = Readonly<{
	engine: string
	interfaces: ToolEngineInterfaceMap
	implementation: BaseToolEngine
}>

function defineToolEngine(definition: {
	engine: string
	implementation: BaseToolEngine
}): ToolEngineDefinition {
	return Object.freeze({
		...definition,
		interfaces: createToolEngineInterfaceMap(definition.implementation),
	})
}

function createToolEngineInterfaceMap(implementation: BaseToolEngine): ToolEngineInterfaceMap {
	let result: Record<string, BaseToolEngineInterface> = {}
	for (let engineInterfaceToken of implementation.getEngineInterfaceTokens()) {
		result[engineInterfaceToken.id] = implementation
	}
	return Object.freeze(result)
}

const formToolEngine = new FormToolEngine()
const itemListToolEngine = new ItemListToolEngine()
const trackerToolEngine = new TrackerToolEngine(formToolEngine)
const graphToolEngine = new GraphToolEngine()
const stagedWorksheetToolEngine = new StagedWorksheetToolEngine()
const timelineToolEngine = new TimelineToolEngine()
const canvasTimelineToolEngine = new CanvasTimelineToolEngine()

const toolEngineRegistry = Object.freeze({
	[ToolEngineEnum.FORM]: defineToolEngine({
		engine: ToolEngineEnum.FORM,
		implementation: formToolEngine,
	}),
	[ToolEngineEnum.ITEM_LIST]: defineToolEngine({
		engine: ToolEngineEnum.ITEM_LIST,
		implementation: itemListToolEngine,
	}),
	[ToolEngineEnum.TRACKER]: defineToolEngine({
		engine: ToolEngineEnum.TRACKER,
		implementation: trackerToolEngine,
	}),
	[ToolEngineEnum.STAGED_WORKSHEET]: defineToolEngine({
		engine: ToolEngineEnum.STAGED_WORKSHEET,
		implementation: stagedWorksheetToolEngine,
	}),
	[ToolEngineEnum.GRAPH]: defineToolEngine({
		engine: ToolEngineEnum.GRAPH,
		implementation: graphToolEngine,
	}),
	[ToolEngineEnum.TIMELINE]: defineToolEngine({
		engine: ToolEngineEnum.TIMELINE,
		implementation: timelineToolEngine,
	}),
	[ToolEngineEnum.CANVAS_TIMELINE]: defineToolEngine({
		engine: ToolEngineEnum.CANVAS_TIMELINE,
		implementation: canvasTimelineToolEngine,
	}),
})

export function getToolEngine(engineId: string = "") {
	return toolEngineRegistry[engineId as keyof typeof toolEngineRegistry] || null
}

export function createToolEngineApi(engineId: string = "", requiredInterfaceIds: readonly string[] = []): ToolEngineApi | null {
	let engineDefinition = getToolEngine(engineId)
	let result: ToolEngineApi | null = null
	if (engineDefinition) {
		result = {}
		for (let interfaceId of requiredInterfaceIds) {
			let interfaceImplementation = engineDefinition.interfaces[interfaceId]
			if (!interfaceImplementation) {
				throw new Error(`Engine interface is not registered: ${engineId}.${interfaceId}`)
			}
			result[interfaceId] = interfaceImplementation
		}
	}
	return result
}

export function getToolEngineRegistry() {
	return toolEngineRegistry
}

export function engineImplementsInterfaces(engineId: string = "", requiredInterfaceIds: readonly string[] = []): boolean {
	let engineDefinition = getToolEngine(engineId)
	return !!engineDefinition && hasRequiredEngineInterfaces(Object.keys(engineDefinition.interfaces), requiredInterfaceIds)
}
